<template>
	<nta-app-navigation title="Notitia Operator">
		<template #default>
			<v-list-item
				v-for="item in sidebarItems"
				:key="item.title"
				class="ma-2"
				:prepend-icon="item.icon"
				:title="symbolsStore.GUILabel(item.title)"
				rounded="xl"
				style="color: rgb(var(--v-theme-on-primary))"
				@click="nav(item)"
			></v-list-item>
			<v-card
				v-if="domainStore.account && Object.keys(domainStore.account).length > 0"
				style="background: transparent; position: absolute; bottom: 45px; color: rgb(var(--v-theme-on-primary))"
				@click="goToAuth"
			>
				<v-divider></v-divider>
				<v-list-item>
					<template #prepend>
						<v-avatar
							:size="40"
							:color="domainStore.account.color"
							:text="domainStore.account?.firstname?.[0] + domainStore.account?.lastname?.[0]"
						/>
					</template>
					<template #default>
						<p class="authName text-subtitle-2">
							{{ domainStore.account?.firstname }} {{ domainStore.account?.lastname }}
						</p>
						<p class="authEmail text-caption mt-1">{{ domainStore.account?.email }}</p>
					</template>
				</v-list-item>
			</v-card>
			<v-card
				style="
					background: transparent;
					position: absolute;
					bottom: 0;
					left: 0;
					color: rgb(var(--v-theme-on-primary));
				"
				width="100%"
			>
				<v-divider v-if="showLanguagePreference"></v-divider>
				<v-list-item v-if="showLanguagePreference" rounded="xl" class="mx-2 mb-0">
					<v-btn variant="text" @click="showLanguagePreferenceModal = true">
						{{ domainStore.languagePreference }}
						<Flag class="ml-2" :lang="domainStore.languagePreference" :tooltip="false" />
					</v-btn>
				</v-list-item>
			</v-card>
		</template>
	</nta-app-navigation>
	<LanguageSelectorModal
		:show="showLanguagePreferenceModal"
		teleport="body"
		@cancel="showLanguagePreferenceModal = false"
	/>
</template>

<script lang="ts">
export interface SidebarItem {
	icon: string;
	title: string;
	value: (domainId: string, dataspaceId?: string) => string;
	condition: (domainId: string, dataspaceId?: string) => boolean;
}
</script>

<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import router from "../router";
import { useDomainStore } from "../store/domain";
import { useSymbolsStore } from "../store/symbols";
import LanguageSelectorModal from "./LanguageSelectorModal.vue";
import Flag from "./Flag.vue";

let betaCaseId: string;

const domainStore = useDomainStore();
const symbolsStore = useSymbolsStore();

const showLanguagePreferenceModal = ref<boolean>(false);
const showLanguagePreference = computed<boolean>(() => {
	return domainStore.domain.id !== undefined;
});

// Combinations where navigation buttons should be hidden
const hiddenNavigationCombinations = [
	{
		domainId: "49eaae15-7af8-4986-a2cc-4b12cbaf5313", // Berufsberatung .dev
		instanceDataspaceId: "f011741f-414b-41d4-981f-83fa7c81923a", // Berufsberatung .dev
	},
	// TODO: Berufsberatung .world
];

const shouldHideNavigation = computed(() => {
	const domainId = router.currentRoute.value.params.domainId as string;
	const instanceDataspaceId = router.currentRoute.value.params.dataspaceId as string;
	const instanceWorkspaceId = router.currentRoute.value.params.workspaceId as string;

	// only hide if we are inside a workspace
	if (!instanceWorkspaceId) return false;

	return hiddenNavigationCombinations.some(
		(combo) => combo.domainId === domainId && combo.instanceDataspaceId === instanceDataspaceId
	);
});

const allSidebarItems = computed(() => [
	{
		icon: "mdi-web",
		title: "88b38f3a-53cd-4f6d-b3b7-19a267c43185", // symbolId Domains
		value: () => "/",
		condition: () => !betaCaseId && !shouldHideNavigation.value,
	},
	{
		icon: "mdi-database",
		title: "fe4caf08-e069-4164-a5a2-5ed34fac2d48", // symbolId Dataspaces
		value: (domainId: string) => `/${domainId}/template/dataspaces`,
		condition: (domainId: string) => !!domainId && !betaCaseId && !shouldHideNavigation.value,
	},
	{
		icon: "mdi-puzzle-edit-outline",
		title: "1f1e7900-59fa-4e7e-9926-d3bf8d0bbe4b", // symbolId Workspaces
		value: (domainId: string, dataspaceId: string) => `/${domainId}/template/${dataspaceId}/template/workspaces`,
		condition: (domainId: string, dataspaceId: string) =>
			!!domainId && !!dataspaceId && !betaCaseId && !shouldHideNavigation.value,
	},
]);

let sidebarItems = ref<SidebarItem[]>([]);

onMounted(() => {
	routerAfterEach();
});

router.afterEach(() => {
	routerAfterEach();
});

function routerAfterEach() {
	betaCaseId = router.currentRoute.value.params.betaCaseId as string;
	const domainId = router.currentRoute.value.params.domainId as string;
	const dataspaceId = router.currentRoute.value.params.dataspaceId as string;
	sidebarItems.value = allSidebarItems.value.filter((item) => item.condition(domainId, dataspaceId)) as SidebarItem[];
}

function nav(item: SidebarItem) {
	const domainId = router.currentRoute.value.params.domainId as string;
	const dataspaceId = router.currentRoute.value.params.dataspaceId as string;
	router.push(item.value(domainId, dataspaceId));
}

function goToAuth() {
	window.open("https://auth.notitia.world/account", "_blank");
}
</script>

<style lang="scss">
.largerAvatar {
	--v-avatar-height: 40px !important;
}

p.authName,
p.authEmail {
	overflow: hidden;
	text-overflow: ellipsis;
	display: -webkit-box;
	-webkit-line-clamp: 1; /* number of lines to show */
	-webkit-box-orient: vertical;
}
</style>
